/**
 * ListItem — selectable row used in every split-panel left list.
 * Selected state: navy3 background + 3px gold left border (canonical).
 * Optional scroll-into-view on select restoration for deep-linked rows.
 */

import React, { useEffect, useRef } from 'react';
import { colors } from './colors';

interface ListItemProps {
  selected: boolean;
  onClick: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode;        // bottom-left small text (date, count, etc.)
  rightContent?: React.ReactNode; // top-right badge/value
  scrollIntoView?: boolean;      // scroll into view once when selected on mount
}

const ListItem: React.FC<ListItemProps> = ({
  selected,
  onClick,
  title,
  subtitle,
  meta,
  rightContent,
  scrollIntoView,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const hasScrolled = useRef(false);

  useEffect(() => {
    if (selected && scrollIntoView && ref.current && !hasScrolled.current) {
      hasScrolled.current = true;
      setTimeout(() => {
        ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 200);
    }
  }, [selected, scrollIntoView]);

  return (
    <div
      ref={ref}
      onClick={onClick}
      style={{
        padding: '12px 16px',
        cursor: 'pointer',
        borderBottom: `1px solid ${colors.navy3}`,
        background: selected ? colors.navy3 : 'transparent',
        borderLeft: `3px solid ${selected ? colors.gold : 'transparent'}`,
        transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = 'rgba(30,53,87,0.4)';
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = 'transparent';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              color: colors.white,
              fontWeight: 600,
              fontSize: '14px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                color: colors.muted,
                fontSize: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginTop: '2px',
              }}
            >
              {subtitle}
            </div>
          )}
          {meta && (
            <div style={{ color: colors.muted, fontSize: '11px', marginTop: '3px' }}>{meta}</div>
          )}
        </div>
        {rightContent && <div style={{ textAlign: 'right', flexShrink: 0 }}>{rightContent}</div>}
      </div>
    </div>
  );
};

export default ListItem;
